import { useCallback, useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

/**
 * Fetches the full per-SKU margin list for the Product Margins tab.
 * Used with sort + limit query params. The Overview Top-5 widget uses
 * a leaner preview hook (useProductMarginsPreview) hard-pinned to
 * sort=margin, limit=5.
 *
 * @param {{period: string, sort: 'margin'|'revenue'|'units', limit: number}}
 */
export default function useProductMargins({ period = '7d', sort = 'margin', limit = 100 } = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMargins = useCallback(async (p, s, lim) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/finance/products/margins', {
        params: { period: p, sort: s, limit: lim },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load product margins';
      setError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMargins(period, sort, limit);
  }, [period, sort, limit, fetchMargins]);

  return { items, total, loading, error, refetch: () => fetchMargins(period, sort, limit) };
}
